import pluralize from "pluralize";
import {FiAlertTriangle, FiInfo} from "react-icons/fi";

import {Alert} from "@/components/alert/Alert.tsx";
import {AlertVariant} from "@/components/alert/Alert.types.ts";
import {ServiceMaintenanceState} from "@/lib/types/checks.ts";

interface ManualMaintenanceAlertProps {
  services: ServiceMaintenanceState[];
  pauseRequired: boolean;
}

export const ManualMaintenanceAlert = ({services, pauseRequired}: ManualMaintenanceAlertProps) => {
  const total = services.length;
  const alreadyUnderMaintenance = services.filter((s) => s.is_under_manual_maintenance).length;
  const notUnderMaintenance = total - alreadyUnderMaintenance;
  const alreadyPaused = services.filter(
    (s) => s.is_under_manual_maintenance && s.pause_under_manual_maintenance
  ).length;
  const notYetPaused = alreadyUnderMaintenance - alreadyPaused;

  const header = `${pluralize("Check", total, true)} selected`;

  let variant: AlertVariant;
  let children: string;

  if (pauseRequired) {
    if (alreadyUnderMaintenance === total) {
      if (alreadyPaused === total) {
        variant = "default";
        children = "All checks are already under maintenance with execution stopped. No changes to apply.";
      } else if (notYetPaused === total) {
        variant = "maintenance";
        children = "All checks are already under maintenance. Check execution will be stopped for all.";
      } else {
        variant = "maintenance";
        children = `All checks are already under maintenance. Check execution will be stopped for ${pluralize("check", notYetPaused, true)} where it is currently still running.`;
      }
    } else if (alreadyUnderMaintenance === 0) {
      variant = "maintenance";
      children = "All checks will be put under maintenance immediately. Check execution will be stopped.";
    } else {
      variant = "maintenance";
      children = `${pluralize("check", notUnderMaintenance, true)} will be put under maintenance immediately. ${pluralize("check", alreadyUnderMaintenance, true)} ${alreadyUnderMaintenance === 1 ? "is" : "are"} already under maintenance. Check execution will be stopped for all checks.`;
    }
  } else {
    if (alreadyUnderMaintenance === total) {
      variant = "default";
      children = "All checks are already under maintenance. No changes to apply.";
    } else if (alreadyUnderMaintenance === 0) {
      variant = "maintenance";
      children = "All checks will be put under maintenance immediately.";
    } else {
      variant = "maintenance";
      children = `${pluralize("check", notUnderMaintenance, true)} will be put under maintenance immediately. ${pluralize("check", alreadyUnderMaintenance, true)} ${alreadyUnderMaintenance === 1 ? "is" : "are"} already under maintenance.`;
    }
  }

  return (
    <Alert
      className="mt-4"
      size={"small"}
      callout={header}
      variant={variant}
      icon={variant === "maintenance" ? FiAlertTriangle : FiInfo}
    >
      {children}
    </Alert>
  );
};
